import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heartbeat, ArrowClockwise, CheckCircle, WarningCircle, Circle, XCircle } from "@phosphor-icons/react";

const PROVIDER_LABELS = {
  claude: "Claude (YadidAi)",
  stripe: "Stripe (Payments)",
  resend: "Resend (Emails)",
};

const STATUS_STYLES = {
  healthy: { icon: CheckCircle, color: "text-emerald-500", label: "Healthy" },
  degraded: { icon: WarningCircle, color: "text-amber-500", label: "Degraded" },
  error: { icon: XCircle, color: "text-destructive", label: "Error" },
  unconfigured: { icon: Circle, color: "text-muted-foreground", label: "Unconfigured" },
  pending: { icon: Circle, color: "text-muted-foreground", label: "Never checked" },
};

function timeAgo(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/**
 * Read-only strip that surfaces yesterday's canary sweep + lets partners fire a
 * fresh one on demand. Mounted at the bottom of Settings so noisy Resend/Stripe
 * quota issues are visible before a partner tries to send a real digest.
 */
export default function IntegrationsCanary() {
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/health/integrations")
      .then((r) => setLatest(r.data.latest))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load integrations status"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/health/integrations/run");
      const next = {};
      for (const r of data.results) next[r.provider] = r;
      setLatest(next);
      const bad = data.results.filter((r) => r.status !== "healthy");
      if (bad.length === 0) toast.success("All integrations healthy");
      else toast.error(`Issue with: ${bad.map((r) => r.provider).join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health check failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-6 md:col-span-2" data-testid="integrations-canary">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow flex items-center gap-1.5">
            <Heartbeat size={12} weight="fill" /> Integrations canary
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Daily 07:30 UTC probe of Claude, Stripe, and Resend. Fire a fresh check anytime.
          </div>
        </div>
        <button
          onClick={runNow}
          disabled={busy}
          data-testid="canary-run-now"
          className="text-[0.6875rem] font-mono uppercase tracking-widest border border-primary/60 text-primary px-3 py-1.5 hover:bg-primary/10 disabled:opacity-60 inline-flex items-center gap-1.5"
        >
          <ArrowClockwise size={12} weight="bold" className={busy ? "animate-spin" : ""} />
          {busy ? "Probing…" : "Run health check"}
        </button>
      </div>

      {loading && <div className="text-sm text-muted-foreground py-4 text-center">Loading canary state…</div>}

      {!loading && latest && (
        <div className="divide-y divide-border border border-border/60" data-testid="canary-strip">
          {Object.keys(PROVIDER_LABELS).map((k) => {
            const rec = latest[k] || { status: "pending" };
            const style = STATUS_STYLES[rec.status] || STATUS_STYLES.error;
            const Icon = style.icon;
            return (
              <div
                key={k}
                className="flex items-center gap-3 px-3 py-2.5"
                data-testid={`canary-row-${k}`}
              >
                <Icon size={16} weight="fill" className={`${style.color} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{PROVIDER_LABELS[k]}</div>
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground truncate">
                    {rec.detail || style.label}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-[0.625rem] font-mono uppercase tracking-widest ${style.color}`}>
                    {style.label}
                  </div>
                  <div className="text-[0.625rem] font-mono text-muted-foreground">
                    {rec.latency_ms != null ? `${rec.latency_ms}ms · ` : ""}{timeAgo(rec.checked_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
